import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SignInButton } from '@clerk/clerk-react';
import {
  CheckCircle,
  ClipboardCheck,
  Facebook,
  HelpCircle,
  Instagram,
  Linkedin,
  MapPin,
  Menu,
  Plane,
  ShieldCheck,
  Twitter,
} from 'lucide-react';

import { getApiBaseUrl } from '../lib/getApiUrl';
import { SuggestionList, useAddressAutocomplete } from './LandingShared';
import type { AddressSuggestion } from './LandingShared';
import { useProgressiveDataService, getTempSessionId } from '../lib/progressiveDataService';

const colors = {
  brandGreen: '#123c3b',
  white: '#ffffff',
  slate900: '#0f172a',
  slate700: '#334155',
  slate600: '#475569',
  slate500: '#64748b',
  slate400: '#94a3b8',
  slate300: '#cbd5e1',
  slate200: '#e2e8f0',
  slate100: '#f1f5f9',
  slate50: '#f8fafc',
  emerald50: '#e6f5ef',
  amber400: '#fbbf24',
};

const spacing = {
  xs: 8,
  sm: 12,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
  xxxl: 56,
};

interface MobileLandingProps {
  onNavigate: (screen: string) => void;
}

const steps = [
  {
    title: 'Accept Your Roofing Bid',
    desc: 'Review multiple quotes from verified contractors and pick the best fit.',
    img: 'https://raw.githubusercontent.com/dan-corebids/CoreBids-Media/3dedc83c27a883b9db348b9c6ae3967293f5893f/Step0.png',
  },
  {
    title: 'Old Roof Removal',
    desc: 'Strip away shingles and underlayment with full cleanup and protection.',
    img: 'https://raw.githubusercontent.com/dan-corebids/CoreBids-Media/3dedc83c27a883b9db348b9c6ae3967293f5893f/Step1.png',
  },
  {
    title: 'Decking Inspection',
    desc: 'Inspect and repair decking to ensure a solid foundation.',
    img: 'https://raw.githubusercontent.com/dan-corebids/CoreBids-Media/3dedc83c27a883b9db348b9c6ae3967293f5893f/Step2.png',
  },
  {
    title: 'Weatherproof Underlayment',
    desc: 'Install waterproof underlayment to keep your home dry.',
    img: 'https://raw.githubusercontent.com/dan-corebids/CoreBids-Media/3dedc83c27a883b9db348b9c6ae3967293f5893f/Step3.png',
  },
  {
    title: 'Professional Installation',
    desc: 'Lay the new roof with pro craftsmanship and the right materials.',
    img: 'https://raw.githubusercontent.com/dan-corebids/CoreBids-Media/3dedc83c27a883b9db348b9c6ae3967293f5893f/Step4.png',
  },
  {
    title: 'Final Inspection & Clean Up',
    desc: 'Inspect, tidy, and confirm your roof is ready for any weather.',
    img: 'https://raw.githubusercontent.com/dan-corebids/CoreBids-Media/3dedc83c27a883b9db348b9c6ae3967293f5893f/Step5.png',
  },
];

const testimonials = [
  {
    quote:
      'CoreBids made my roof replacement so easy! The estimate was fast and the contractor was professional. Highly recommend!',
    author: '— John D., Nashville',
    rating: 5,
  },
  {
    quote:
      'I got three bids for my roofing project and saved over $2,000. The process was simple and the team was helpful every step of the way.',
    author: '— Maria S., Brentwood',
    rating: 5,
  },
  {
    quote:
      "The online dashboard made it easy to track my project and communicate with my contractor. I'll use CoreBids again!",
    author: '— Steve R., Franklin',
    rating: 5,
  },
  {
    quote:
      'Amazing service! Got my roof estimate in under 24 hours and saved $1,500 compared to other quotes.',
    author: '— Sarah M., Hendersonville',
    rating: 5,
  },
  {
    quote:
      'As a first-time homeowner, I was nervous about getting roof work done. CoreBids made the whole process transparent and stress-free.',
    author: '— Michael T., Murfreesboro',
    rating: 5,
  },
];

const faqs = [
  {
    q: 'Do you need to visit my home?',
    a: 'No. We use remote measurements first. Contractors visit only after you pick a bid.',
  },
  {
    q: 'How fast do I get bids?',
    a: 'Most homeowners see bids within 24–48 hours after entering their address.',
  },
  {
    q: 'Do I have to accept a bid?',
    a: 'No obligation. You can review, ask questions, or walk away anytime.',
  },
  {
    q: 'Do you offer financing?',
    a: 'Yes. We offer flexible financing options so you can spread payments over time if you qualify.',
  },
  {
    q: 'How does payment work?',
    a: 'Your payment is held securely in escrow and only released after you approve completed work.',
  },
];

const roofCards = [
  {
    key: 'asphalt',
    title: 'Asphalt Shingles',
    teaser: 'Cost-effective, fast install, versatile styles.',
    img: '/media/asphalt-roof.png',
    desc:
      'Most common and cost-effective. Good durability, fast install, and lots of color options. Great for most homes and budgets.',
  },
  {
    key: 'metal',
    title: 'Metal Roofing',
    teaser: 'Long-lasting, energy efficient, great in storms.',
    img: '/media/metal-roof.png',
    desc:
      'Long-lasting and energy efficient. Handles wind and weather well, sheds snow easily, and can lower cooling costs over time.',
  },
  {
    key: 'tile',
    title: 'Tile Roofing',
    teaser: 'Premium look with excellent longevity.',
    img: '/media/tile-roof.png',
    desc:
      'Premium look with excellent longevity. Great for hot climates and upscale homes. Heavier material, so structure matters.',
  },
  {
    key: 'flat',
    title: 'Flat Roof',
    teaser: 'Common for additions; membrane systems (TPO/EPDM).',
    img: '/media/flat-roof.png',
    desc:
      'Common for modern designs and additions. Uses membranes like TPO/EPDM for waterproofing. Easy to service and maintain.',
  },
] as const;

const MobileLanding: React.FC<MobileLandingProps> = ({ onNavigate }) => {
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const {
    address,
    suggestions,
    showSuggestions,
    // CB:BEGIN remove-loading-indicator
    // isLoadingSuggestions, // Removed - no longer showing loading indicator
    // CB:END remove-loading-indicator
    handleAddressChange,
    handleSuggestionSelect,
    activeIndex,
    moveSelection,
    selectActive,
    setActiveIndex,
  } = useAddressAutocomplete(apiBaseUrl);
  
  // CB:BEGIN save-address-to-temp-homeowner
  // Save address to temp homeowner immediately when selected
  const { createOrUpdateTempHomeowner } = useProgressiveDataService();
  
  // Override handleSuggestionSelect to save address immediately
  const handleSuggestionSelectWithSave = useCallback(async (suggestion: AddressSuggestion) => {
    const label =
      (suggestion?.description ||
      suggestion?.structured_formatting?.main_text ||
      '').trim();
    
    // Only proceed if we have a valid address
    if (!label) {
      console.warn('⚠️ No address label found in suggestion:', suggestion);
      return;
    }
    
    handleSuggestionSelect(suggestion);
    
    // CB:BEGIN save-address-on-selection-mobile
    // Save to localStorage
    try {
      localStorage.setItem('enteredAddress', label);
      // Clear stale address keys to prevent overwriting
      localStorage.removeItem('pendingUserAddress');
      localStorage.removeItem('last_address');
      console.log('✅ Address saved to localStorage:', label);
    } catch (error) {
      console.warn('⚠️ Failed to save address to localStorage:', error);
    }
    
    // Save to temp homeowner immediately
    try {
      const tempSessionId = getTempSessionId();
      if (tempSessionId && label) {
        await createOrUpdateTempHomeowner({
          temp_session_id: tempSessionId,
          address: label,
        });
        console.log('✅ Address saved to temp homeowner:', label);
      } else {
        console.warn('⚠️ Cannot save address - missing tempSessionId or label:', { tempSessionId, label });
      }
    } catch (error) {
      console.warn('⚠️ Failed to save address to temp homeowner:', error);
    }
    // CB:END save-address-on-selection-mobile
  }, [handleSuggestionSelect, createOrUpdateTempHomeowner]);
  // CB:END save-address-to-temp-homeowner

  const [showSticky, setShowSticky] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [activeStepIdx, setActiveStepIdx] = useState(0);
  const stepTimer = useRef<number | null>(null);
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const [testimonialTransitionEnabled, setTestimonialTransitionEnabled] = useState(true);
  const [flippedRoofs, setFlippedRoofs] = useState<Record<string, boolean>>({});

  const analyticsEnabled = useMemo(() => {
    const env = (import.meta as any)?.env || {};
    if (env.VITE_DISABLE_ANALYTICS === 'true') return false;
    if (apiBaseUrl.includes('api.dev.corebids.com')) return env.VITE_ALLOW_DEV_ANALYTICS === 'true';
    return true;
  }, [apiBaseUrl]);

  const track = useCallback(
    (eventName: string, meta?: Record<string, any>) => {
      if (!analyticsEnabled) return;
      try {
        const payload = { eventName, page: 'mobile-landing', ts: Date.now(), meta: meta || {} };
        const endpoint = '/api/analytics/events';
        if (navigator.sendBeacon) {
          const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
          navigator.sendBeacon(endpoint, blob);
        } else {
          fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            keepalive: true,
          }).catch(() => {});
        }
      } catch {
        // ignore
      }
    },
    [analyticsEnabled]
  );

  const handleSubmit = useCallback(async () => {
    const addressToSave = address.trim();
    
    // CB:BEGIN save-address-to-temp-homeowner-on-submit
    // Save address to localStorage and temp homeowner when submitting
    try {
      if (addressToSave) {
        localStorage.setItem('enteredAddress', addressToSave);
        
        // Save to temp homeowner immediately
        try {
          const tempSessionId = getTempSessionId();
          if (tempSessionId) {
            await createOrUpdateTempHomeowner({
              temp_session_id: tempSessionId,
              address: addressToSave,
            });
            console.log('✅ Address saved to temp homeowner on submit:', addressToSave);
          }
        } catch (error) {
          console.warn('⚠️ Failed to save address to temp homeowner on submit:', error);
        }
      }
    } catch (error) {
      console.warn('⚠️ Failed to save address to localStorage:', error);
    }
    // CB:END save-address-to-temp-homeowner-on-submit
    
    track('mobile_address_submit');
    onNavigate('project-wizard');
  }, [address, onNavigate, track, createOrUpdateTempHomeowner]);

  const handleSuggestionPick = useCallback(
    (sug: AddressSuggestion) => {
      handleSuggestionSelectWithSave(sug);
      track('mobile_address_pick');
    },
    [handleSuggestionSelectWithSave, track]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        moveSelection('down');
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        moveSelection('up');
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        if (showSuggestions && activeIndex >= 0) {
          selectActive();
          return;
        }
        handleSubmit();
      }
    },
    [moveSelection, showSuggestions, activeIndex, selectActive, handleSubmit]
  );

  useEffect(() => {
    const onScroll = () => setShowSticky(window.scrollY > 250);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    stepTimer.current = window.setInterval(() => {
      setActiveStepIdx((prev) => (prev + 1) % steps.length);
    }, 5000);
    return () => {
      if (stepTimer.current) window.clearInterval(stepTimer.current);
    };
  }, []);

  useEffect(() => {
    const t = window.setInterval(() => setCurrentTestimonialIndex((p) => p + 1), 5000);
    return () => window.clearInterval(t);
  }, []);

  // Load Enhancify payment calculator script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://www.enhancify.com/paymentcalculatorwidget/';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup: remove script on unmount
      const existingScript = document.querySelector('script[src="https://www.enhancify.com/paymentcalculatorwidget/"]');
      if (existingScript && existingScript.parentNode) {
        existingScript.parentNode.removeChild(existingScript);
      }
    };
  }, []);

  const loopedTestimonials = useMemo(() => {
    if (testimonials.length < 2) return testimonials;
    return [...testimonials, ...testimonials.slice(0, 2)];
  }, []);

  const handleTestimonialTransitionEnd = useCallback(() => {
    if (!testimonialTransitionEnabled) return;
    if (currentTestimonialIndex >= testimonials.length) {
      setTestimonialTransitionEnabled(false);
      setCurrentTestimonialIndex(0);
      requestAnimationFrame(() => requestAnimationFrame(() => setTestimonialTransitionEnabled(true)));
    }
  }, [currentTestimonialIndex, testimonialTransitionEnabled, testimonials.length]);

  return (
    <div
      style={{
        fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
        minHeight: '100vh',
        background: colors.white,
        color: colors.slate900,
        paddingBottom: 90,
      }}
    >
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 50,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: `${spacing.md}px ${spacing.lg}px`,
          background: colors.white,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        }}
      >
        <div
          style={{ fontSize: 20, fontWeight: 700, color: colors.brandGreen, cursor: 'pointer' }}
          onClick={() => onNavigate('landing')}
        >
          CoreBids
        </div>

        <button
          type="button"
          aria-label="Menu"
          onClick={() => setMenuOpen((v) => !v)}
          style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            border: `1px solid ${colors.slate200}`,
            background: colors.emerald50,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <Menu size={24} color={colors.brandGreen} />
        </button>

        {menuOpen && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              right: spacing.lg,
              marginTop: spacing.sm,
              background: colors.white,
              borderRadius: 12,
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              padding: spacing.md,
              minWidth: 220,
              zIndex: 100,
              border: `1px solid rgba(15,23,42,0.10)`,
            }}
          >
            <button
              style={menuItemStyle}
              onClick={() => {
                setMenuOpen(false);
          const el = document.getElementById('faqs');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          else onNavigate('faq');
        }}
            >
              FAQs
            </button>

            <div style={{ height: 1, background: 'rgba(15, 23, 42, 0.08)', margin: '8px 0' }} />

            <button
              style={menuItemStyle}
              onClick={() => {
                setMenuOpen(false);
                window.open('https://www.enhancify.com/corebids', '_blank', 'noopener,noreferrer');
              }}
            >
              Financing
            </button>

            <div style={{ height: 1, background: 'rgba(15, 23, 42, 0.08)', margin: '8px 0' }} />

            <button
              style={menuItemStyle}
              onClick={() => {
                setMenuOpen(false);
                onNavigate('contractor-landing');
              }}
            >
              Join as a contractor
              <div style={{ fontSize: 12, fontWeight: 500, color: colors.slate600, marginTop: 2 }}>
                Create your profile and bid
              </div>
            </button>

            <div style={{ height: 1, background: 'rgba(15, 23, 42, 0.08)', margin: '8px 0' }} />

            <SignInButton mode="modal">
              <button
                style={menuItemStyle}
                onClick={() => {
                  setMenuOpen(false);
                  track('mobile_menu_login_click');
                }}
              >
                Login
              </button>
            </SignInButton>
          </div>
        )}
      </header>

      <section
        id="hero"
        style={{
          position: 'relative',
          minHeight: '55vh',
          padding: `${spacing.xxl}px ${spacing.lg}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <video
          autoPlay
          loop
          muted
          playsInline
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        >
          <source src="https://raw.githubusercontent.com/dan-corebids/CoreBids-Media/9dbec8143c1605a75d43df73874cf9ad510b0b3c/WaitlistVid.mp4" type="video/mp4" />
        </video>

        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.8), rgba(0,0,0,0.7), rgba(0,0,0,0.6))',
          }}
        />

        <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 380, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: spacing.xl }}>
          <h1
            style={{
              fontSize: 34,
              fontWeight: 900,
              lineHeight: 1.15,
              textAlign: 'center',
              color: colors.white,
              margin: 0,
              textShadow: '0 4px 12px rgba(0,0,0,0.8)',
            }}
          >
            Online Roofing Estimates
            <br />
            <span style={{ fontStyle: 'italic' }}>from Real Pros</span>
          </h1>

          <div style={{ width: '100%', maxWidth: 360, position: 'relative' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                background: colors.white,
                borderRadius: 14,
                padding: 5,
                boxShadow: '0 8px 32px rgba(0,0,0,0.24)',
                position: 'relative',
                // CB:BEGIN remove-gray-circle-container
                // Ensure no background circles or arcs appear
                backgroundImage: 'none',
                // CB:END remove-gray-circle-container
              }}
              className="address-input-container"
            >
                  <input
                    value={address}
                    onChange={(e) => handleAddressChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onFocus={() => track('mobile_address_focus')}
                    placeholder="Enter your address"
                    name="cb_address"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    data-lpignore="true"
                    data-form-type="other"
                style={{
                  flex: 1,
                  height: 64,
                  padding: `0 ${spacing.md}px`,
                  border: 'none',
                  outline: 'none',
                  fontSize: 16,
                  color: colors.slate900,
                  background: 'transparent',
                  borderRadius: 10,
                  minWidth: 0,
                }}
              />

                <button
                  onClick={handleSubmit}
                  disabled={address.trim().length < 6}
                aria-label="Continue"
                  className="continue-button-black"
                  style={{
                  padding: `0 ${spacing.md}px`,
                  height: 64,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: colors.brandGreen,
                  border: 'none',
                  borderRadius: 10,
                  cursor: address.trim().length < 6 ? 'not-allowed' : 'pointer',
                  opacity: address.trim().length < 6 ? 0.5 : 1,
                  fontSize: 16,
                  fontWeight: 500,
                  color: '#000000',
                }}
              >
                <span style={{ color: '#000000' }}>Continue!</span>
                </button>
              </div>

            <div style={{ position: 'absolute', left: 0, right: 0, top: '100%', marginTop: 10, zIndex: 30 }}>
              <div style={{ opacity: showSuggestions ? 1 : 0, pointerEvents: showSuggestions ? 'auto' : 'none', transform: showSuggestions ? 'translateY(0)' : 'translateY(-4px)', transition: 'all 150ms ease-out' }}>
                  <SuggestionList
                    suggestions={suggestions}
                    visible={showSuggestions}
                    onSelect={handleSuggestionPick}
                    query={address}
                    activeIndex={activeIndex}
                    setActiveIndex={setActiveIndex}
                  />
              </div>
            </div>

            {/* CB:BEGIN remove-loading-indicator */}
            {/* Loading indicator removed - no gray circle */}
            {/* CB:END remove-loading-indicator */}
          </div>
        </div>
      </section>

      <section style={{ padding: `${spacing.xxl}px ${spacing.lg}px`, background: colors.white }}>
        <div style={{ display: 'flex', justifyContent: 'space-around', gap: spacing.lg }}>
              {[
                { Icon: Plane, label: 'Remote measurements' },
                { Icon: ShieldCheck, label: 'Verified contractors' },
                { Icon: CheckCircle, label: 'No obligation' },
          ].map(({ Icon, label }) => (
            <div key={label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: spacing.md, flex: 1 }}>
              <div
                style={{
                  width: 100,
                  height: 100,
                  borderRadius: 999,
                  background: colors.emerald50,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                }}
              >
                <Icon size={60} color={colors.brandGreen} strokeWidth={2} />
                    </div>
              <div style={{ fontSize: 14, fontWeight: 600, textAlign: 'center', color: colors.slate900, lineHeight: 1.4 }}>
                      {label}
                    </div>
            </div>
          ))}
          </div>
        </section>

      <section style={{ padding: `${spacing.xxxl}px ${spacing.lg}px`, background: colors.white }}>
        <h2 style={{ fontSize: 32, fontWeight: 900, color: colors.brandGreen, margin: 0, lineHeight: 1.2 }}>
            What Our Customers Say
          </h2>

        <div style={{ marginTop: spacing.xl, overflow: 'hidden' }}>
            <div
              style={{
              display: 'flex',
              transform: `translateX(-${currentTestimonialIndex * 100}%)`,
                transitionProperty: testimonialTransitionEnabled ? 'transform' : 'none',
                transitionDuration: testimonialTransitionEnabled ? '700ms' : '0ms',
                transitionTimingFunction: 'ease-out',
              }}
              onTransitionEnd={handleTestimonialTransitionEnd}
            >
            {loopedTestimonials.map((t, idx) => (
              <div key={`${t.author}-${idx}`} style={{ minWidth: '100%', paddingRight: 8 }}>
                <div
                  style={{
                    borderRadius: 24,
                    background: colors.white,
                    border: `2px solid ${colors.slate200}`,
                    boxShadow: '0 8px 24px rgba(15,23,42,0.08)',
                    padding: spacing.xl,
                    minHeight: 280,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: spacing.md,
                  }}
                >
                  <div style={{ display: 'flex', gap: 4, fontSize: 24, color: colors.amber400 }}>
                    {'★★★★★'}
                    </div>

                  <div style={{ fontSize: 17, fontStyle: 'italic', color: colors.slate700, lineHeight: 1.65, flex: 1 }}>
                    “{t.quote}”
                  </div>

                  <div style={{ fontSize: 15, fontWeight: 600, color: colors.brandGreen }}>{t.author}</div>
                  </div>
                </div>
              ))}
            </div>

          <div style={{ display: 'flex', justifyContent: 'center', gap: spacing.sm, marginTop: spacing.lg }}>
            {testimonials.map((_, idx) => {
              const active = currentTestimonialIndex % testimonials.length === idx;
              return (
                <button
                  key={idx}
                  aria-label={`Go to testimonial ${idx + 1}`}
                  onClick={() => setCurrentTestimonialIndex(idx)}
                  style={{ width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', padding: 0 }}
                >
                  <div
                    style={{
                      width: active ? 28 : 10,
                      height: 10,
                      borderRadius: 999,
                      background: active ? colors.brandGreen : colors.slate300,
                      transition: 'all 0.3s ease',
                    }}
                  />
                </button>
              );
            })}
            </div>
          </div>
        </section>

      <section style={{ padding: `${spacing.xxxl}px ${spacing.lg}px`, background: colors.white }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
              <ClipboardCheck size={24} color="#10b981" />
              <div style={{ fontSize: 20, fontWeight: 700, color: colors.slate900 }}>Project steps</div>
            </div>
            <div style={{ marginTop: spacing.xs, fontSize: 14, color: colors.slate600, lineHeight: 1.5 }}>
                  The key steps our vetted contractors follow so you know what to expect.
              </div>
            </div>
          <div style={{ fontSize: 14, color: colors.slate500 }}>{activeStepIdx + 1}/{steps.length}</div>
                      </div>

        <div style={{ marginTop: spacing.lg, overflow: 'hidden', borderRadius: 16, border: `1px solid ${colors.slate200}`, background: colors.white }}>
          <div style={{ display: 'flex', transform: `translateX(-${activeStepIdx * 100}%)`, transition: 'transform 500ms ease-out' }}>
            {steps.map((s) => (
              <div key={s.title} style={{ minWidth: '100%', padding: spacing.lg }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
                  <div style={{ width: '100%', aspectRatio: '4/3', background: colors.slate100, borderRadius: 12, overflow: 'hidden' }}>
                    <img src={s.img} alt={s.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
                      </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
                    <ClipboardCheck size={20} color="#10b981" />
                    <div style={{ fontSize: 18, fontWeight: 700, color: colors.slate900 }}>{s.title}</div>
                  </div>

                  <div style={{ fontSize: 14, color: colors.slate600, lineHeight: 1.6 }}>{s.desc}</div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </section>

      <section style={{ padding: `${spacing.xxxl}px ${spacing.lg}px`, background: colors.white }}>
        <h2 style={{ fontSize: 32, fontWeight: 900, color: colors.brandGreen, margin: 0, lineHeight: 1.2 }}>
            Why customers love CoreBids.
          </h2>

        <div style={{ marginTop: spacing.lg, fontSize: 18, color: colors.slate600, lineHeight: 1.6 }}>
            Every day, homeowners rely on CoreBids to compare bids from trusted pros—without the spam,
            pressure, or endless back-and-forth.
            </div>

        <div style={{ marginTop: spacing.xl, display: 'flex', flexDirection: 'column', gap: spacing.lg }}>
          {[
            {
              title: 'Get to a hire faster.',
              desc: 'Enter your address once and get multiple bids with remote measurements—no site visit needed to start comparing.',
            },
            {
              title: 'Only see local, trusted pros.',
              desc: 'We vet reputable local contractors so you can review bids with confidence and avoid low-quality providers.',
            },
            {
              title: 'No spam. No pressure.',
              desc: 'No contractor calls or pushy messages. You choose the bid you want, when you want—and move forward on your terms.',
            },
          ].map((f) => (
            <div key={f.title} style={{ display: 'flex', gap: spacing.md, padding: spacing.lg, borderRadius: 28, border: `1px solid ${colors.slate300}`, background: colors.white }}>
              <div style={{ width: 48, height: 48, minWidth: 48, borderRadius: 12, background: colors.emerald50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ width: 24, height: 24, borderRadius: 999, background: colors.brandGreen }} />
                  </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 20, fontWeight: 900, color: colors.slate900 }}>{f.title}</div>
                <div style={{ marginTop: spacing.sm, fontSize: 16, color: colors.slate600, lineHeight: 1.6 }}>{f.desc}</div>
                  </div>
                </div>
          ))}
          </div>
        </section>

      <section style={{ padding: `${spacing.xxxl}px ${spacing.lg}px`, background: colors.white }}>
        <h2 style={{ fontSize: 28, fontWeight: 900, color: colors.brandGreen, margin: 0 }}>
            What roof is right for you?
          </h2>

        <div style={{ marginTop: spacing.lg, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.md }}>
            {roofCards.map((card) => {
            const flipped = !!flippedRoofs[card.key];
              return (
                <button
                  key={card.key}
                onClick={() => setFlippedRoofs((p) => ({ ...p, [card.key]: !p[card.key] }))}
                    style={{
                  aspectRatio: '4/3',
                  border: `1px solid ${colors.slate200}`,
                  borderRadius: 28,
                                  overflow: 'hidden',
                  position: 'relative',
                  padding: 0,
                  background: colors.white,
                  cursor: 'pointer',
                  minHeight: 44,
                }}
                aria-label={`Roof type: ${card.title}. Tap to ${flipped ? 'flip back' : 'learn more'}.`}
              >
                {!flipped ? (
                  <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                    <img src={card.img} alt={card.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.6), rgba(0,0,0,0.1), rgba(0,0,0,0))' }} />
                    <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: spacing.md, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: spacing.sm }}>
                      <div style={{ flex: 1, textAlign: 'left' }}>
                        <div style={{ fontSize: 14, fontWeight: 800, color: colors.white, textShadow: '0 2px 8px rgba(0,0,0,0.6)' }}>
                                {card.title}
                            </div>
                            </div>
                      <div style={{ padding: '6px 12px', background: 'rgba(0,0,0,0.4)', borderRadius: 999, backdropFilter: 'blur(6px)', fontSize: 11, color: 'rgba(255,255,255,0.95)', whiteSpace: 'nowrap' }}>
                        Learn more →
                          </div>
                          </div>
                        </div>
                ) : (
                          <div
                            style={{
                      width: '100%',
                      height: '100%',
                      padding: `${spacing.sm}px ${spacing.md}px ${spacing.md}px`,
                      display: 'flex',
                      flexDirection: 'column',
                      textAlign: 'left',
                    }}
                  >
                    <div style={{ marginTop: 0, fontSize: 12, color: colors.slate700, lineHeight: 1.4, flex: 1 }}>
                      {card.desc}
                          </div>
                    <div style={{ marginTop: spacing.sm, fontSize: 11, color: colors.slate500, textAlign: 'right' }}>
                      Flip back →
                        </div>
                      </div>
                )}
              </button>
            );
          })}
                    </div>
      </section>

      <section id="faqs" style={{ padding: `${spacing.xxxl}px ${spacing.lg}px`, background: colors.slate50 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
          <HelpCircle size={24} color={colors.slate500} />
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: colors.brandGreen }}>FAQs</h2>
        </div>

        <div style={{ marginTop: spacing.lg, display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
          {faqs.map((f, idx) => {
            const open = openFaq === idx;
            return (
              <div key={f.q} style={{ background: colors.white, border: `1px solid ${colors.slate200}`, borderRadius: 12, overflow: 'hidden' }}>
                <button
                  onClick={() => {
                    setOpenFaq(open ? null : idx);
                    track('mobile_faq_expand', { faq: f.q, open: !open });
                  }}
                      style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: spacing.md,
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    minHeight: 44,
                    textAlign: 'left',
                  }}
                >
                  <span style={{ fontSize: 15, fontWeight: 600, color: colors.slate900 }}>{f.q}</span>
                  <span style={{ fontSize: 24, fontWeight: 700, color: colors.amber400, marginLeft: spacing.sm }}>
                    {open ? '−' : '+'}
                  </span>
                </button>

                {open && (
                  <div style={{ padding: `0 ${spacing.md}px ${spacing.md}px ${spacing.md}px`, fontSize: 14, color: colors.slate600, lineHeight: 1.6 }}>
                    {f.a}
                        </div>
                )}
                          </div>
              );
            })}
          </div>
        </section>

      {/* Financing Section */}
      <section style={{ padding: `${spacing.xxxl}px ${spacing.lg}px`, background: colors.white }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.xs, marginBottom: spacing.lg }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: colors.brandGreen }}>Flexible Financing Options</h2>
        </div>

        <div style={{ marginTop: spacing.md, fontSize: 15, color: colors.slate600, lineHeight: 1.6, marginBottom: spacing.xl }}>
          CoreBids partners with Enhancify to offer flexible financing solutions for your roofing project. 
          Get approved quickly and choose a payment plan that fits your budget. Calculate your monthly payments below 
          and explore financing options to make your roof replacement more affordable.
        </div>

        {/* Payment Calculator Widget */}
        <div style={{
          marginBottom: spacing.xl,
          width: '100%'
        }}>
          <div 
            id='paymentcalculatorwidget' 
            data-defaultscheme='false' 
            data-color1='#68BA62' 
            data-color2='#1C418C' 
            data-cobrandedcolor='#FFFFFF' 
            data-border='false' 
            data-page='9931908' 
            data-hidelink='0'
            style={{
              width: '100%'
            }}
          />
        </div>

        {/* Enhancify Financing Image */}
        <div style={{
          width: '100%',
          marginTop: spacing.lg,
          position: 'relative'
        }}>
          <a
            href="https://www.enhancify.com/corebids?utm_source=We_Offer_Financing_Mobile&from=https%3A%2F%2Fwww.corebids.com%2F"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              position: 'relative',
              display: 'block',
              width: '100%',
              textDecoration: 'none',
              borderRadius: '8px',
              overflow: 'hidden'
            }}
          >
            <img
              src="/enhancify-financing-mobile.png"
              alt="Enhancify Financing Options"
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
                borderRadius: '8px'
              }}
            />
            <span style={{
              position: 'absolute',
              bottom: '12px',
              left: '16px',
              color: '#ffffff',
              fontSize: '16px',
              fontWeight: 'bold',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
              pointerEvents: 'auto',
              zIndex: 10,
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}>
              Click Here!
            </span>
          </a>
        </div>
      </section>

      <section style={{ padding: `${spacing.xxxl}px ${spacing.lg}px`, background: colors.slate50 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
          <MapPin size={24} color="#10b981" />
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: colors.slate900 }}>Where do we service?</h2>
        </div>

        <div style={{ marginTop: spacing.sm, fontSize: 14, color: colors.slate600, lineHeight: 1.6 }}>
          Serving homeowners nationwide, CoreBids matches your project with vetted local contractors—no matter where you're located.
        </div>
        </section>

      <footer style={{ padding: spacing.xl, background: '#123c3b', color: colors.slate100 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.lg }}>
          {[
            { title: 'Company', links: [{ t: 'About Us', href: '/AboutUs' }, { t: 'Contact Us', href: '/contact' }] },
            { title: 'For Homeowners', links: [{ t: 'How It Works', href: '/faq#how-it-works' }, { t: 'Pricing', href: '/pricing' }, { t: 'FAQ', href: '/faq' }] },
            { title: 'For Contractors', links: [{ t: 'Join Our Network', href: '/contractor-landing' }, { t: 'Contractor Benefits', href: '/contractor-landing#contractor-faq' }, { t: 'Support', href: '/support' }] },
            { title: 'Legal', links: [{ t: 'Terms of Service', href: '/terms' }, { t: 'Privacy Policy', href: '/privacy' }, { t: 'Accessibility', href: '/accessibility' }] },
          ].map((col) => (
            <div key={col.title} style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: colors.white }}>{col.title}</div>
              {col.links.map((l) => (
                <a 
                  key={l.t} 
                  href={l.href} 
                  target={l.href === '/terms' || l.href === '/accessibility' ? '_blank' : undefined}
                  rel={l.href === '/terms' || l.href === '/accessibility' ? 'noopener noreferrer' : undefined}
                  onClick={(e) => {
                    if (l.href === '/AboutUs') {
                      e.preventDefault();
                      onNavigate('about');
                    } else if (l.href === '/contractor-landing#contractor-faq') {
                      e.preventDefault();
                      onNavigate('contractor-landing');
                      setTimeout(() => {
                        const el = document.getElementById('contractor-faq');
                        if (el) {
                          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }, 100);
                    } else if (l.href === '/faq#how-it-works') {
                      e.preventDefault();
                      onNavigate('faq');
                      setTimeout(() => {
                        const el = document.getElementById('how-it-works');
                        if (el) {
                          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }, 100);
                    }
                  }}
                  style={{ fontSize: 14, color: colors.slate300, textDecoration: 'none', minHeight: 32, display: 'flex', alignItems: 'center' }}
                >
                  {l.t}
                </a>
              ))}
                </div>
          ))}
            </div>

        <div style={{ marginTop: spacing.xl, paddingTop: spacing.md, borderTop: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'center', gap: spacing.lg }}>
          {[
            { Icon: Facebook, href: 'https://facebook.com/corebids', label: 'Facebook' },
            { Icon: Instagram, href: 'https://instagram.com/corebids', label: 'Instagram' },
            { Icon: Twitter, href: 'https://twitter.com/corebids', label: 'Twitter' },
            { Icon: Linkedin, href: 'https://linkedin.com/company/corebids', label: 'LinkedIn' },
          ].map(({ Icon, href, label }) => (
            <a key={label} href={href} target="_blank" rel="noopener noreferrer" style={{ minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }} aria-label={label}>
              <Icon size={24} color={colors.slate300} />
            </a>
          ))}
            </div>

        <div style={{ marginTop: spacing.md, paddingTop: spacing.md, borderTop: '1px solid rgba(255,255,255,0.1)', textAlign: 'center', fontSize: 12, color: colors.slate400 }}>
              © 2025 CoreBids. All rights reserved. | Serving Nashville, Knoxville, and Chattanooga
            </div>
      </footer>

      {showSticky && (
        <div
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 40,
            background: colors.white,
            borderTop: `1px solid ${colors.slate200}`,
            boxShadow: '0 -4px 12px rgba(0,0,0,0.10)',
            padding: `${spacing.md}px ${spacing.lg}px`,
            paddingBottom: `calc(${spacing.md}px + env(safe-area-inset-bottom))`,
            display: 'flex',
            alignItems: 'center',
            gap: spacing.md,
          }}
        >
          <div style={{ flex: 1, fontSize: 14, fontWeight: 600, color: colors.slate900, lineHeight: 1.3 }}>
            Enter your address to get your estimate
          </div>

          <button
            onClick={handleSubmit}
            disabled={address.trim().length < 6}
            style={{
              padding: `${spacing.sm}px ${spacing.lg}px`,
              background: colors.brandGreen,
              color: colors.white,
              border: 'none',
              borderRadius: 999,
              fontSize: 16,
              fontWeight: 700,
              cursor: address.trim().length < 6 ? 'not-allowed' : 'pointer',
              opacity: address.trim().length < 6 ? 0.5 : 1,
              minHeight: 44,
              boxShadow: '0 4px 12px rgba(18,60,59,0.2)',
            }}
          >
            Start
          </button>
        </div>
      )}
    </div>
  );
};

const menuItemStyle: React.CSSProperties = {
  width: '100%',
  textAlign: 'left',
  padding: `${spacing.md}px`,
  border: 'none',
  background: 'transparent',
  fontSize: 16,
  fontWeight: 600,
  color: colors.slate900,
  cursor: 'pointer',
  minHeight: 44,
};

export default MobileLanding;

